import { LockChecker } from '@sparemin/embed-config';
import { TEMPLATE_LOCK_MESSAGE } from 'constants/locks';
import { IVideoClip } from 'types';
import { KEY_TEXT_OVERLAY_SLOTS } from './constants';
import {
  CaptionsConfig,
  DisabledConfig,
  KeySlideshowConfig,
  KeyTextOverlayConfig,
  Lockable,
  TextOverlayConfig,
  VideoClipsConfig,
  VideoTemplateEditorFeatures,
} from './types';

type SlideshowLockSlot = keyof KeySlideshowConfig;

const SLIDESHOW_LOCK_SLOTS: SlideshowLockSlot[] = ['mainImage'];

export const DEFAULT_LOCK_REASON = TEMPLATE_LOCK_MESSAGE;

function makeDisabled(reason: string): DisabledConfig {
  return { disabled: true, reason };
}

function computeCaptionsOverride(
  lock: LockChecker,
  reason: string,
): CaptionsConfig | undefined {
  if (lock.isFullyLocked('captions')) {
    return undefined;
  }

  const disabled = makeDisabled(reason);
  const cfg: CaptionsConfig = {};

  if (!lock.can('captions', 'style')) {
    cfg.style = disabled;
  } else if (!lock.can('captions', 'style.fontFamily')) {
    cfg.style = { fontFamily: disabled };
  }

  if (!lock.can('captions', 'position')) {
    cfg.position = disabled;
  }

  if (!lock.can('captions', 'size')) {
    cfg.size = disabled;
  }

  return Object.keys(cfg).length > 0 ? cfg : undefined;
}

/**
 * Watermark is modeled as a single all-or-nothing lock on the editor side.
 * The api-sdk exposes finer caps (remove/image/position/size) but every UI
 * surface — tile, child view, canvas drag/resize — gates on the same state.
 * Returns a `DisabledConfig` (carrying the supplied lock reason) rather than
 * the bare `'locked'` so the tile can surface a lockspec-specific tooltip
 * without clobbering the legacy admin message produced by
 * `watermarkReadOnlySelector`.
 */
function computeWatermarkOverride(
  lock: LockChecker,
  reason: string,
): DisabledConfig | undefined {
  return lock.isFullyLocked('watermark') ? makeDisabled(reason) : undefined;
}

// Per-slot resolution against textOverlayInfo per-instance entries, addressed
// by textType. isFullyLocked('textOverlayInfo') is intentionally NOT used as
// a short-circuit: per-instance entries can override the asset-type default
// in either direction, so each slot is queried independently.
//
// Whole-style locks (`{ style: false }` at the LockSpec level) are not
// surfaced as their own feature-state — disabling the entire style group
// would block the modal from opening, which is the only way to edit the
// text content itself. A whole-style LockSpec still implicitly locks
// fontFamily because `lock.can(..., 'style.fontFamily', ...)` walks down
// through the locked parent.
function computeTextOverlayOverride(
  lock: LockChecker,
  reason: string,
): KeyTextOverlayConfig | undefined {
  const disabled = makeDisabled(reason);
  const cfg: KeyTextOverlayConfig = {};

  KEY_TEXT_OVERLAY_SLOTS.forEach(slot => {
    const slotCfg: TextOverlayConfig = {};

    if (!lock.can('textOverlayInfo', 'style.fontFamily', { textType: slot })) {
      slotCfg.style = { fontFamily: disabled };
    }

    if (Object.keys(slotCfg).length > 0) {
      cfg[slot] = slotCfg;
    }
  });

  return Object.keys(cfg).length > 0 ? cfg : undefined;
}

// Editor-side feature shape collapses per-property granularity into a single
// all-or-nothing flag per slot. The api-sdk exposes finer caps
// (remove/image/style/position/size) for runtime targeting and future use,
// but every UI consumer that lives behind ImageChildView (delete, crop,
// replace, blur, border-radius) plus the canvas drag/resize controls all
// read the same flag — locking entry to the child view covers them in one
// shot. The slot is only marked DISABLED when *all* the per-slot caps are
// locked; partial locks at the api-sdk layer don't surface in the editor.
function computeSlideshowImageOverride(
  lock: LockChecker,
  reason: string,
): Lockable | KeySlideshowConfig | undefined {
  if (lock.isFullyLocked('slideshowInfo')) {
    return 'locked';
  }

  const disabled = makeDisabled(reason);
  const cfg: KeySlideshowConfig = {};

  SLIDESHOW_LOCK_SLOTS.forEach(slot => {
    const slotIsFullyLocked =
      !lock.can('slideshowInfo', 'remove', { imageType: slot }) &&
      !lock.can('slideshowInfo', 'image', { imageType: slot }) &&
      !lock.can('slideshowInfo', 'style', { imageType: slot }) &&
      !lock.can('slideshowInfo', 'position', { imageType: slot }) &&
      !lock.can('slideshowInfo', 'size', { imageType: slot });

    if (slotIsFullyLocked) {
      cfg[slot] = disabled;
    }
  });

  return Object.keys(cfg).length > 0 ? cfg : undefined;
}

// Editor-side feature shape collapses per-property granularity into a single
// all-or-nothing flag per snapshotted index. The api-sdk exposes finer caps
// (remove/style/position/size) for runtime targeting and future use, but
// every UI consumer that lives behind the per-clip child view (delete, crop,
// replace, blur) plus the canvas drag/resize/zoom controls all read the same
// flag — locking entry to the child view covers them in one shot. A slot is
// only marked DISABLED when *all* the per-slot caps are locked; partial
// locks at the api-sdk layer don't surface in the editor. Asset-type-level
// `add` is intentionally not mapped — parallel to slideshow's removal of
// `canAdd`.
function computeVideoClipsOverride(
  lock: LockChecker,
  videoClips: IVideoClip[],
  reason: string,
): Lockable | VideoClipsConfig | undefined {
  if (lock.isFullyLocked('videoClips')) {
    return 'locked';
  }

  const disabled = makeDisabled(reason);
  const byIndex: VideoClipsConfig['byIndex'] = {};

  videoClips.forEach((_clip, index) => {
    const slotIsFullyLocked =
      !lock.can('videoClips', 'remove', { index }) &&
      !lock.can('videoClips', 'style', { index }) &&
      !lock.can('videoClips', 'position', { index }) &&
      !lock.can('videoClips', 'size', { index });

    if (slotIsFullyLocked) {
      byIndex[index] = disabled;
    }
  });

  if (Object.keys(byIndex).length === 0) {
    return undefined;
  }

  return { byIndex };
}

function computeLockFeatureOverrides(
  lock: LockChecker,
  videoClips: IVideoClip[],
  reason: string,
): Partial<VideoTemplateEditorFeatures> {
  const overrides: Partial<VideoTemplateEditorFeatures> = {};

  const captions = computeCaptionsOverride(lock, reason);
  if (captions) {
    overrides.captions = captions;
  }

  const textOverlay = computeTextOverlayOverride(lock, reason);
  if (textOverlay) {
    overrides.textOverlay = textOverlay;
  }

  const watermark = computeWatermarkOverride(lock, reason);
  if (watermark) {
    overrides.watermark = watermark;
  }

  const slideshowImage = computeSlideshowImageOverride(lock, reason);
  if (slideshowImage) {
    overrides.slideshowImage = slideshowImage;
  }

  const videoClipsOverride = computeVideoClipsOverride(
    lock,
    videoClips,
    reason,
  );
  if (videoClipsOverride) {
    overrides.videoClips = videoClipsOverride;
  }

  return overrides;
}

function mergeLockFeatureOverrides(
  baseFeatures: VideoTemplateEditorFeatures,
  lockFeatureOverrides: Partial<VideoTemplateEditorFeatures>,
): VideoTemplateEditorFeatures {
  const features = { ...baseFeatures };

  // -- Captions ---------------------------------------------------------------

  // the base captions feature is either pro-gated or hidden.  so, if there's any
  // baseFeatures configuration, we don't want to overwrite it
  const { captions } = baseFeatures;

  if (
    captions !== 'hidden' &&
    captions !== 'pro-gated' &&
    lockFeatureOverrides.captions
  ) {
    features.captions = lockFeatureOverrides.captions;
  }

  // -- Text overlays ----------------------------------------------------------
  // No pre-state to defend against today (textOverlay has no
  // hidden/pro-gated/coming-soon variant). Apply the lock-derived overrides
  // directly when present.
  if (lockFeatureOverrides.textOverlay) {
    features.textOverlay = lockFeatureOverrides.textOverlay;
  }

  // -- Watermark --------------------------------------------------------------
  // The legacy `watermarkReadOnlySelector` (applied upstream) may have already
  // set `features.watermark = 'locked'` to surface the "Your administrator has
  // locked this option" tooltip. Skip the lockspec-derived override in that
  // case so the admin message wins; otherwise apply the DisabledConfig so the
  // tile shows the caller-supplied lock reason.
  if (baseFeatures.watermark !== 'locked' && lockFeatureOverrides.watermark) {
    features.watermark = lockFeatureOverrides.watermark;
  }

  // -- Slideshow image --------------------------------------------------------
  // No pre-state to defend against today (slideshowImage has no
  // hidden/pro-gated/coming-soon variant). Apply the lock-derived overrides
  // directly when present.
  if (lockFeatureOverrides.slideshowImage) {
    features.slideshowImage = lockFeatureOverrides.slideshowImage;
  }

  // -- Video clips ------------------------------------------------------------
  // No pre-state to defend against today (videoClips has no
  // hidden/pro-gated/coming-soon variant). Apply the lock-derived overrides
  // directly when present.
  if (lockFeatureOverrides.videoClips) {
    features.videoClips = lockFeatureOverrides.videoClips;
  }

  return features;
}

export function overrideFeaturesFromLockChecker(
  features: VideoTemplateEditorFeatures,
  lockChecker: LockChecker,
  videoClips: IVideoClip[],
  reason: string = DEFAULT_LOCK_REASON,
) {
  return mergeLockFeatureOverrides(
    features,
    computeLockFeatureOverrides(lockChecker, videoClips, reason),
  );
}
